import { useCallback, useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import type { Dimensions } from '@shipstation/client-common/types/dimensions';
import type { Weight } from '@shipstation/client-common/types/weight';

import { USD } from '../../../../../../constants/currency-codes';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { useEnableMultiPackageItemAllocation } from '../../../../../../feature-flags/hooks/use-enable-multi-package-item-allocation';
import { STANDARD_PACKAGE_ID } from '../../../../../../services/package-service';
import Global from '../../../../../../store/global-selectors';
import { updateFulfillmentPlanEntitiesOptimistically } from '../../../../../../store/entities/entities.actions';
import { useFulfillmentPlannerContext } from '../../../../../../fulfillment-planner/context';
import { populatePackagesWithAssignments } from '../helpers/extract-item-assignments';
import type { PackageWithTareWeight } from '../utils/calculate-package-weight';
import { calculatePackageWeight } from '../utils/calculate-package-weight';
import type { AssignedItem, Package, OrderItem } from '../../../../../../components/modals/multi-package-modal-dnd/types';

import { logPackingRecommendationActivity, PackingRecommendationAction } from '../../../../../../packing/services/packing-activity-service';
import {
  trackPackingRecommendationApplied,
  trackPackingRecommendationDismissed,
} from '../../../../../../packing/services/packing-recommendations-analytics';

import { messages } from '../messages';
import type { BoxResult } from '../types';
import { buildBoxesFromPlanPackages } from '../utils/build-boxes-from-plan-packages';
import type { SellerPackage } from '../utils/build-boxes-from-plan-packages';
import {
  getAppliedState,
  getCachedResult,
  hasError,
  isCurrentlyCalculating,
  isPersistedAppliedDismissed,
  markPackingApplied,
  markPersistedAppliedDismissed,
  onAppliedChange,
  resetPackingApplied,
} from '../use-packing-applied';
import {
  getTerminalPackingRecommendationStatus,
  PackingRecommendationStatus,
} from '../../../../../../packing/services/packing-recommendation-status';
import type { JobResultResponse } from '../../../../../../packing/services/packing-service';
import type { PackingRecommendationGuard } from '../../../../../../packing/enforcement/use-packing-recommendation-guard';

const DIMENSION_MATCH_TOLERANCE = 0.5;

const sortFn = (a: AssignedItem, b: AssignedItem) => a.orderItemId.localeCompare(b.orderItemId);
const assignmentsChanged = (applied: AssignedItem[], current: AssignedItem[]): boolean => {
  if (applied.length !== current.length) return true;
  const sortedApplied = [...applied].sort(sortFn);
  const sortedCurrent = [...current].sort(sortFn);
  return sortedApplied.some((a, i) => a.orderItemId !== sortedCurrent[i].orderItemId || a.quantity !== sortedCurrent[i].quantity);
};

type SignatureItem = {
  fulfillmentPlanItemId?: string;
  orderItemId?: string;
  salesOrderItemId?: string;
  quantity?: number;
};

const buildItemsSignature = (items: readonly SignatureItem[]): string =>
  JSON.stringify(
    items
      .map((i) => ({ id: i.fulfillmentPlanItemId ?? i.orderItemId ?? i.salesOrderItemId, q: i.quantity ?? 0 }))
      .sort((a, b) => String(a.id).localeCompare(String(b.id))),
  );

type FulfillmentPlannerContextCallbacks = {
  onMultiPackageSave?: (packages: unknown[]) => void;
};

type UseApplyRecommendationReturn = {
  handleApply: () => void;
  isApplied: boolean;
  appliedPackageName: string | null;
  resetApplied: () => void;
  logDismissOnce: () => void;
};

export const useApplyRecommendation = (
  result: JobResultResponse | null,
  availablePackages: Array<Record<string, unknown>>,
  fulfillmentPlanId: string,
  sellerDimensionUnit: string,
  sellerWeightUnit: string,
  orderItems: OrderItem[] = [],
  packingRecommendationGuard: PackingRecommendationGuard,
  currentPackagesOverride?: Package[],
): UseApplyRecommendationReturn => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const context = useFulfillmentPlannerContext();
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const currentPackages = (currentPackagesOverride ?? fulfillmentPlan?.packages ?? emptyArray) as Array<{
    packageId?: string;
    weight?: { value?: number };
    dimensions?: Record<string, number>;
    assignedItems?: AssignedItem[];
    insuredValue?: Package['insuredValue'];
    contentDescription?: string;
  }>;

  const persistedState = getAppliedState(fulfillmentPlanId);
  const backendStatus = getTerminalPackingRecommendationStatus(fulfillmentPlan);
  const isBackendApplied = backendStatus === PackingRecommendationStatus.Applied && !isPersistedAppliedDismissed(fulfillmentPlanId);
  const [isApplied, setIsApplied] = useState(persistedState?.isApplied ?? isBackendApplied);
  const [appliedPackageName, setAppliedPackageName] = useState<string | null>(persistedState?.packageName ?? null);
  const [appliedValues, setAppliedValues] = useState<Array<{
    packageId?: string;
    weight?: number;
    dimensions?: Record<string, number>;
  }> | null>(persistedState?.appliedPackages ?? null);
  const isApplyingRef = useRef(false);
  const appliedPackageNameRef = useRef<string | null>(persistedState?.packageName ?? null);
  const hasDismissedRef = useRef(false);
  const hasGuardedChangeRef = useRef(false);
  const hasCheckedInitialDivergenceRef = useRef(false);

  const clearAppliedState = useCallback(() => {
    setIsApplied(false);
    setAppliedPackageName(null);
    setAppliedValues(null);
    isApplyingRef.current = false;
    appliedPackageNameRef.current = null;
    hasDismissedRef.current = false;
    hasGuardedChangeRef.current = false;
  }, []);

  useEffect(() => {
    return onAppliedChange((fpId, packageName) => {
      if (fpId !== fulfillmentPlanId) return;
      if (packageName === null) {
        clearAppliedState();
        return;
      }
      setIsApplied(true);
      setAppliedPackageName(packageName);
      appliedPackageNameRef.current = packageName;
      hasDismissedRef.current = false;
      setAppliedValues(getAppliedState(fulfillmentPlanId)?.appliedPackages ?? null);
    });
  }, [fulfillmentPlanId, clearAppliedState]);

  const { onMultiPackageSave } = (context ?? emptyObject) as FulfillmentPlannerContextCallbacks;
  const isMultiPackageItemAllocationEnabled = useEnableMultiPackageItemAllocation();

  useEffect(() => {
    const existing = getAppliedState(fulfillmentPlanId);
    if (backendStatus !== PackingRecommendationStatus.Applied) {
      if (existing?.hydratedFromBackend) {
        resetPackingApplied(fulfillmentPlanId);
      }
      return;
    }
    if (existing || isPersistedAppliedDismissed(fulfillmentPlanId)) return;
    if (isCurrentlyCalculating(fulfillmentPlanId) || getCachedResult(fulfillmentPlanId) !== null || hasError(fulfillmentPlanId)) return;
    if (currentPackages.length === 0) return;

    const planItems = (fulfillmentPlan?.items ?? emptyArray) as OrderItem[];
    const boxes = buildBoxesFromPlanPackages(currentPackages, availablePackages as SellerPackage[], planItems);
    const isMultiBox = currentPackages.length > 1;
    const packageName = isMultiBox
      ? intl.formatMessage(messages.packagesConfigured, { count: currentPackages.length })
      : intl.formatMessage(messages.packageConfigured, { name: boxes[0]?.box?.name ?? '' });

    markPackingApplied({
      fulfillmentPlanId,
      packageName,
      isMultiBox,
      boxes,
      appliedPackages: currentPackages.map((p) => ({ packageId: p.packageId, weight: p.weight?.value, dimensions: p.dimensions })),
      appliedAssignments: currentPackages.map((p) => p.assignedItems ?? []),
      hydratedFromBackend: true,
      appliedItemsSignature: buildItemsSignature(planItems),
    });
  }, [fulfillmentPlanId, backendStatus, currentPackages, availablePackages, fulfillmentPlan, intl]);

  const findPackageByBox = useCallback(
    (box: BoxResult['box']) => {
      if (!box) return null;

      if (box.packageId) {
        const byId = availablePackages.find((pkg: { packageId?: string }) => pkg.packageId === box.packageId);
        if (byId) return byId;
      }

      if (box.name) {
        const byName = availablePackages.find((pkg: { name?: string }) => pkg.name?.toLowerCase() === box.name?.toLowerCase());
        if (byName) return byName;
      }

      if (box.length && box.width && box.height) {
        const byDimensions = availablePackages.find(
          (pkg: { dimensions?: Record<string, unknown>; length?: number; width?: number; height?: number }) => {
            const pkgDims = pkg.dimensions ?? {};
            const pkgLength = Number(pkgDims.length) || Number(pkg.length) || 0;
            const pkgWidth = Number(pkgDims.width) || Number(pkg.width) || 0;
            const pkgHeight = Number(pkgDims.height) || Number(pkg.height) || 0;

            return (
              Math.abs(pkgLength - box.length) <= DIMENSION_MATCH_TOLERANCE &&
              Math.abs(pkgWidth - box.width) <= DIMENSION_MATCH_TOLERANCE &&
              Math.abs(pkgHeight - box.height) <= DIMENSION_MATCH_TOLERANCE
            );
          },
        );
        if (byDimensions) return byDimensions;
      }

      return null;
    },
    [availablePackages],
  );

  const formatBoxDimensions = useCallback(
    (box: BoxResult['box']) => {
      if (!box) return null;
      return {
        length: Math.round(box.length * 10) / 10,
        width: Math.round(box.width * 10) / 10,
        height: Math.round(box.height * 10) / 10,
        unit: sellerDimensionUnit,
      };
    },
    [sellerDimensionUnit],
  );

  const buildCompositePackageId = useCallback(
    (selectedPackage: { packageTypeId?: string; isCustom?: boolean; carrierId?: number } | null) => {
      if (!selectedPackage) {
        return `${STANDARD_PACKAGE_ID}-false-0`;
      }
      return `${selectedPackage.packageTypeId}-${selectedPackage.isCustom ?? false}-${selectedPackage.carrierId ?? 0}`;
    },
    [],
  );

  const convertBoxToPackage = useCallback(
    (boxResult: BoxResult, existingPackage?: { insuredValue?: Package['insuredValue']; contentDescription?: string }): Package | null => {
      const box = boxResult?.box;
      if (!box) return null;

      const selectedPackage = findPackageByBox(box);
      const dimensions = formatBoxDimensions(box);
      const weight = calculatePackageWeight(boxResult.itemPositions, selectedPackage as PackageWithTareWeight | null, sellerWeightUnit);

      return {
        packageId: (selectedPackage?.packageId as string) ?? buildCompositePackageId(selectedPackage),
        packageTypeId: (selectedPackage?.packageTypeId as string) ?? STANDARD_PACKAGE_ID,
        carrierId: (selectedPackage?.carrierId as number) ?? null,
        isCustom: (selectedPackage?.isCustom as boolean) ?? true,
        name: (selectedPackage?.name as string) ?? box.name,
        packageTypeName: (selectedPackage?.name as string) ?? box.name,
        dimensions: dimensions as Dimensions,
        weight: weight as Weight,
        insuredValue: existingPackage?.insuredValue ?? { value: 0, code: USD },
        contentDescription: existingPackage?.contentDescription,
      };
    },
    [findPackageByBox, formatBoxDimensions, sellerWeightUnit, buildCompositePackageId],
  );

  const handleApply = useCallback(() => {
    if (!result) {
      return;
    }

    const applyBoxes: BoxResult[] = result?.boxes ?? [];
    const isMultiBox = (result?.isMultiBox ?? false) && applyBoxes.length > 0;
    const packageName = isMultiBox
      ? intl.formatMessage(messages.packagesConfigured, { count: applyBoxes.length })
      : intl.formatMessage(messages.packageConfigured, { name: result?.optimalBox?.name ?? '' });
    const firstExistingPackage = currentPackages[0];

    let packagesToSave: Package[] = [];

    if (isMultiBox) {
      const packages = applyBoxes.map((box) => convertBoxToPackage(box)).filter((pkg): pkg is NonNullable<typeof pkg> => pkg !== null);
      if (!packages.length || !onMultiPackageSave) return;

      packagesToSave = isMultiPackageItemAllocationEnabled ? populatePackagesWithAssignments(packages, result, orderItems) : packages;
      onMultiPackageSave(packagesToSave);

      isApplyingRef.current = true;
    } else {
      const optimalBox = result?.optimalBox;
      if (!optimalBox || !onMultiPackageSave) return;

      const singleBoxResult = { box: optimalBox, itemPositions: result?.itemPositions ?? [] };
      const pkg = convertBoxToPackage(singleBoxResult, firstExistingPackage);
      if (!pkg) return;

      packagesToSave = isMultiPackageItemAllocationEnabled ? populatePackagesWithAssignments([pkg], result, orderItems) : [pkg];
      onMultiPackageSave(packagesToSave);

      isApplyingRef.current = true;
    }

    setAppliedPackageName(packageName ?? null);
    appliedPackageNameRef.current = packageName ?? null;
    hasDismissedRef.current = false;
    setIsApplied(true);

    const singleBoxEntry = result.optimalBox ? [{ box: result.optimalBox, itemPositions: result.itemPositions }] : [];

    const preApplyPackages = currentPackages.map((p) => ({
      ...p,
      assignedItems: p.assignedItems ? [...p.assignedItems] : undefined,
    })) as unknown as Package[];
    markPackingApplied({
      fulfillmentPlanId,
      packageName: packageName ?? '',
      isMultiBox,
      boxes: isMultiBox ? applyBoxes : singleBoxEntry,
      appliedAssignments: isMultiPackageItemAllocationEnabled ? packagesToSave.map((p) => p.assignedItems ?? []) : undefined,
      preApplyPackages,
      appliedItemsSignature: buildItemsSignature((fulfillmentPlan?.items ?? emptyArray) as OrderItem[]),
    });

    logPackingRecommendationActivity({
      fulfillmentPlanId,
      action: PackingRecommendationAction.Applied,
      packageName: packageName ?? '',
    });
    trackPackingRecommendationApplied(fulfillmentPlanId, packageName ?? '', isMultiBox, isMultiBox ? applyBoxes.length : 1);
  }, [
    result,
    fulfillmentPlanId,
    intl,
    convertBoxToPackage,
    onMultiPackageSave,
    orderItems,
    currentPackages,
    isMultiPackageItemAllocationEnabled,
    fulfillmentPlan,
  ]);

  const resetApplied = useCallback(() => {
    const preApplyPackages = getAppliedState(fulfillmentPlanId)?.preApplyPackages;
    resetPackingApplied(fulfillmentPlanId);
    markPersistedAppliedDismissed(fulfillmentPlanId);
    clearAppliedState();

    if (preApplyPackages && preApplyPackages.length > 0 && onMultiPackageSave) {
      isApplyingRef.current = true;
      onMultiPackageSave(preApplyPackages);
    }
  }, [fulfillmentPlanId, clearAppliedState, onMultiPackageSave]);

  const patchPersistedStatus = useCallback(
    (status: PackingRecommendationStatus) => {
      if (!fulfillmentPlan) return;
      dispatch(
        updateFulfillmentPlanEntitiesOptimistically(
          [fulfillmentPlanId],
          [{ ...fulfillmentPlan, packingRecommendationStatus: status, packingRecommendationFailureReason: null }],
        ),
      );
    },
    [dispatch, fulfillmentPlan, fulfillmentPlanId],
  );

  const logDismissOnce = useCallback(() => {
    if (hasDismissedRef.current) return;
    hasDismissedRef.current = true;
    logPackingRecommendationActivity({
      fulfillmentPlanId,
      action: PackingRecommendationAction.Dismissed,
      packageName: appliedPackageNameRef.current ?? '',
    });
    trackPackingRecommendationDismissed(fulfillmentPlanId, appliedPackageNameRef.current ?? '');
    patchPersistedStatus(PackingRecommendationStatus.Dismissed);
  }, [fulfillmentPlanId, patchPersistedStatus]);

  const currentItemsSignature = buildItemsSignature((fulfillmentPlan?.items ?? emptyArray) as OrderItem[]);
  useEffect(() => {
    if (!isApplied) return;
    const entry = getAppliedState(fulfillmentPlanId);
    if (!entry?.appliedItemsSignature || entry.appliedItemsSignature === currentItemsSignature) return;
    resetPackingApplied(fulfillmentPlanId);
    patchPersistedStatus(PackingRecommendationStatus.Stale);
  }, [isApplied, currentItemsSignature, fulfillmentPlanId, patchPersistedStatus]);

  // Rebuilds the applied package(s) from the originally-applied boxes and re-saves them.
  const revertToApplied = useCallback(() => {
    const currentState = getAppliedState(fulfillmentPlanId);
    if (!currentState || !onMultiPackageSave) return;

    const packagesToRevert = currentState.boxes
      .map((box) => convertBoxToPackage(box, currentState.isMultiBox ? undefined : currentPackages[0]))
      .filter((pkg): pkg is NonNullable<typeof pkg> => pkg !== null)
      .map((pkg, idx) => {
        const appliedWeight = currentState.appliedPackages?.[idx]?.weight;
        return {
          ...pkg,
          weight: appliedWeight != null ? { value: appliedWeight, unit: sellerWeightUnit } : pkg.weight,
          assignedItems: currentState.appliedAssignments?.[idx] ?? pkg.assignedItems,
        };
      });

    if (!packagesToRevert.length) return;

    isApplyingRef.current = true;
    onMultiPackageSave(packagesToRevert);
  }, [fulfillmentPlanId, onMultiPackageSave, convertBoxToPackage, currentPackages, sellerWeightUnit]);

  // Change detection: reset applied state when user manually changes package/weight/dimensions
  useEffect(() => {
    const packages = currentPackages;
    const currentPackageCount = packages.length;

    if (isApplyingRef.current) {
      const snapshot = packages.map((p: { packageId?: string; weight?: { value?: number }; dimensions?: Record<string, number> }) => ({
        packageId: p.packageId,
        weight: p.weight?.value,
        dimensions: p.dimensions,
      }));
      setAppliedValues(snapshot);
      const currentState = getAppliedState(fulfillmentPlanId);
      if (currentState) {
        markPackingApplied({
          fulfillmentPlanId,
          packageName: currentState.packageName,
          isMultiBox: currentState.isMultiBox,
          boxes: currentState.boxes,
          appliedPackages: snapshot,
          appliedAssignments: currentState.appliedAssignments,
        });
      }
      isApplyingRef.current = false;
      return;
    }

    if (!hasCheckedInitialDivergenceRef.current) {
      hasCheckedInitialDivergenceRef.current = true;
      return;
    }

    if (!isApplied || !appliedValues) {
      return;
    }

    const packageCountChanged = currentPackageCount !== appliedValues.length;
    const storedAppliedAssignments = getAppliedState(fulfillmentPlanId)?.appliedAssignments;

    const hasFieldChange = packages.some(
      (
        p: { packageId?: string; weight?: { value?: number }; dimensions?: Record<string, number>; assignedItems?: AssignedItem[] },
        idx: number,
      ) => {
        const saved = appliedValues[idx];
        if (!saved) return true;
        if (p.packageId !== saved.packageId) return true;
        if (p.weight?.value !== undefined && saved.weight !== undefined && Math.abs(p.weight.value - saved.weight) > 0.01) return true;
        if (
          p.dimensions &&
          saved.dimensions &&
          (Math.abs((p.dimensions.length ?? 0) - (saved.dimensions.length ?? 0)) > 0.1 ||
            Math.abs((p.dimensions.width ?? 0) - (saved.dimensions.width ?? 0)) > 0.1 ||
            Math.abs((p.dimensions.height ?? 0) - (saved.dimensions.height ?? 0)) > 0.1)
        )
          return true;
        const appliedForPackage = storedAppliedAssignments?.[idx];
        if (appliedForPackage !== undefined && assignmentsChanged(appliedForPackage, p.assignedItems ?? [])) return true;
        return false;
      },
    );

    const hasAnyChange = packageCountChanged || hasFieldChange;

    if (hasAnyChange) {
      if (hasGuardedChangeRef.current) return;
      hasGuardedChangeRef.current = true;
      packingRecommendationGuard.guardOverride(
        () => {
          hasGuardedChangeRef.current = false;
          logDismissOnce();
          resetApplied();
        },
        appliedPackageNameRef.current ?? '',
        () => {
          hasGuardedChangeRef.current = false;
          revertToApplied();
        },
      );
    }
  }, [
    currentPackages,
    isApplied,
    appliedValues,
    fulfillmentPlanId,
    resetApplied,
    logDismissOnce,
    packingRecommendationGuard,
    revertToApplied,
  ]);

  return { handleApply, isApplied, appliedPackageName, resetApplied, logDismissOnce };
};
